import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Clock, Mail, Home, ArrowRight } from 'lucide-react';

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [countdown, setCountdown] = useState(10);
  
  const paymentType = searchParams.get('type') || 'addon';
  const amount = searchParams.get('amount') || '0';
  const ref = searchParams.get('ref') || '';
  const email = searchParams.get('email') || '';

  useEffect(() => {
    // Only auto-redirect for addon payments
    if (paymentType === 'addon') {
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            navigate('/instructor/storage');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [navigate, paymentType]);

  const getTypeLabel = () => {
    if (paymentType === 'addon') return 'Add-on (พื้นที่เพิ่ม)';
    if (paymentType === 'subscription') return 'แพ็กเกจสมาชิก';
    return 'การชำระเงิน';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Success Icon */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-green-500 rounded-full mb-6 animate-bounce">
            <CheckCircle className="w-16 h-16 text-white" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
            ✅ ส่งสลิปสำเร็จ!
          </h1>
          <p className="text-lg text-gray-600">
            เราได้รับการชำระเงินของคุณแล้ว
          </p>
        </div>

        {/* Payment Details Card */}
        <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 mb-6">
          <div className="border-b pb-4 mb-4">
            <h2 className="text-xl font-bold text-gray-900 mb-2">รายละเอียดการชำระเงิน</h2>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">ประเภท:</span>
                <span className="font-semibold text-gray-900">{getTypeLabel()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">จำนวนเงิน:</span>
                <span className="text-2xl font-bold text-green-600">฿{parseInt(amount).toLocaleString()}</span>
              </div>
              {ref && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">เลขอ้างอิง:</span>
                  <span className="font-mono font-semibold text-gray-900 text-sm">{ref}</span>
                </div>
              )}
            </div>
          </div>

          {/* Status Timeline */}
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">ส่งสลิปสำเร็จ</h3>
                <p className="text-sm text-gray-600">เราได้รับสลิปการโอนเงินของคุณแล้ว</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">รอการตรวจสอบ</h3>
                <p className="text-sm text-gray-600">ทีมงานกำลังตรวจสอบการชำระเงินของคุณ</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                <Mail className="w-6 h-6 text-gray-400" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">รับการยืนยัน</h3>
                <p className="text-sm text-gray-600">คุณจะได้รับอีเมลยืนยันภายใน 24 ชั่วโมง</p>
              </div>
            </div>
          </div>
        </div>

        {/* Special Instructions for Subscription */}
        {paymentType === 'subscription' && email && (
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-6 mb-6 border-2 border-blue-200">
            <div className="flex items-start gap-3">
              <Mail className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-blue-900 mb-2">📧 สำคัญ! กรุณาสมัครสมาชิกด้วยอีเมล:</h3>
                <div className="bg-white rounded-lg p-3 mb-3 border border-blue-200">
                  <p className="text-blue-800 font-bold text-lg text-center">{decodeURIComponent(email)}</p>
                </div>
                <ul className="space-y-2 text-sm text-blue-800">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600">1.</span>
                    <span>สมัครสมาชิกด้วย <strong>อีเมลเดียวกัน</strong> เพื่อเชื่อมโยงการชำระเงิน</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600">2.</span>
                    <span>รอ Admin อนุมัติการชำระเงิน (ภายใน 24 ชม.)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600">3.</span>
                    <span>ยืนยันอีเมลของคุณ</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600">4.</span>
                    <span>เข้าใช้งานได้เต็มรูปแบบ</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Information Card */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 mb-6 border-2 border-blue-200">
          <div className="flex items-start gap-3">
            <Clock className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-blue-900 mb-2">⏰ ระยะเวลาการตรวจสอบ</h3>
              <ul className="space-y-2 text-sm text-blue-800">
                <li className="flex items-start gap-2">
                  <span className="text-blue-600">•</span>
                  <span>ทีมงานจะตรวจสอบภายใน <strong>24 ชั่วโมง</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600">•</span>
                  <span>คุณจะได้รับอีเมลยืนยันเมื่อการชำระเงินได้รับการอนุมัติ</span>
                </li>
                {paymentType === 'addon' && (
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600">•</span>
                    <span>พื้นที่เพิ่มเติมจะถูกเพิ่มให้อัตโนมัติทันทีหลังอนุมัติ</span>
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          {paymentType === 'subscription' ? (
            <>
              <button
                onClick={() => navigate('/admin/unified-payment-approval')}
                className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 text-white py-4 rounded-xl font-bold text-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
              >
                <Clock className="w-5 h-5" />
                ไปที่ Admin Dashboard (รออนุมัติ)
              </button>

              <button
                onClick={() => navigate('/login')}
                className="w-full bg-white text-gray-700 py-4 rounded-xl font-semibold text-lg hover:bg-gray-50 transition-all flex items-center justify-center gap-2 border-2 border-gray-200"
              >
                เข้าสู่ระบบ (ถ้ามีบัญชีแล้ว)
                <ArrowRight className="w-5 h-5" />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => navigate('/instructor/storage')}
                className="w-full bg-gradient-to-r from-blue-600 to-pink-600 text-white py-4 rounded-xl font-bold text-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
              >
                <Home className="w-5 h-5" />
                ไปหน้า Storage Dashboard
              </button>

              <button
                onClick={() => navigate('/instructor/dashboard')}
                className="w-full bg-white text-gray-700 py-4 rounded-xl font-semibold text-lg hover:bg-gray-50 transition-all flex items-center justify-center gap-2 border-2 border-gray-200"
              >
                ไปหน้า Dashboard
                <ArrowRight className="w-5 h-5" />
              </button>
            </>
          )}
        </div>

        {/* Auto Redirect Notice - Only for addon */}
        {paymentType === 'addon' && (
          <div className="text-center mt-6">
            <p className="text-sm text-gray-500">
              จะพาคุณไปหน้า Storage Dashboard อัตโนมัติใน <span className="font-bold text-blue-600">{countdown}</span> วินาที
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
